import React, { useState } from 'react';
import { Plus, Loader2, Check, Car, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { productsAPI } from '../../services/api';
import CompatRuleEditor from './CompatRuleEditor';

// Merge inherited rules into form.compatibility (mode 'merge' | 'replace')
export const mergeRulesInto = (f, newRules, mode) => {
  const merged = mode === 'replace' ? [] : [...f.compatibility];
  newRules.forEach(nr => {
    const ex = merged.find(r => r.brand === nr.brand);
    if (!ex) { merged.push({ brand: nr.brand, models: nr.models, fuels: [], engine_names: nr.engine_names }); return; }
    if (ex.models.length) ex.models = [...new Set([...ex.models, ...nr.models])];
    ex.engine_names = [...new Set([...(ex.engine_names || []), ...nr.engine_names])];
  });
  return { ...f, compatibility: merged };
};

const ProductCompatSection = ({ form, setForm, brands, productId, onFitmentsRefresh }) => {
  const [syncing, setSyncing] = useState(false);
  const updateRule = (idx, rule) => setForm(f => ({ ...f, compatibility: f.compatibility.map((r, i) => (i === idx ? rule : r)) }));
  const removeRule = (idx) => setForm(f => ({ ...f, compatibility: f.compatibility.filter((_, i) => i !== idx) }));
  const addRule = () => setForm(f => ({ ...f, compatibility: [...f.compatibility, { brand: '', models: [], fuels: [], engine_names: [] }] }));

  const inheritCompat = async () => {
    setSyncing(true);
    try {
      const res = await productsAPI.compatSync(productId);
      if (res.pending) {
        setForm(f => ({ ...f, compat_pending: { rules: Array(res.rules_added).fill(0), models_added: res.models_added, fitments_added: res.fitments_added, sources: res.sources } }));
        toast.info(`Fiche sensible : ${res.rules_added} règle(s), ${res.models_added} modèle(s) et ${res.fitments_added} motorisation(s) proposées — à valider dans Produits › Compatibilités héritées.`);
        setSyncing(false);
        return;
      }
      setForm(f => ({ ...mergeRulesInto(f, res.compatibility, 'merge'), compat_sync: { at: new Date().toISOString(), sources: res.sources }, compat_pending: null }));
      if (res.fitments_added > 0) onFitmentsRefresh();
      if (!res.equivalents) toast.info('Aucune pièce équivalente trouvée (références ou liens manuels).');
      else if (!res.changed && !res.sources.length) toast.info(`${res.equivalents} pièce(s) équivalente(s), mais aucune n'a de compatibilité renseignée.`);
      else if (!res.changed) toast.success(`Déjà à jour : ${res.sources.length} pièce(s) équivalente(s) avec compatibilité.`);
      else toast.success(`Compatibilité complétée depuis ${res.sources.length} pièce(s) équivalente(s) : +${res.rules_added} marque(s), +${res.models_added} modèle(s), +${res.fitments_added} motorisation(s).`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Synchronisation impossible'); }
    setSyncing(false);
  };

  const pending = form.compat_pending;
  const sources = form.compat_sync?.sources || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2"><Car size={18} className="text-orange-500" /> Compatibilité véhicules</h3>
          <p className="text-xs text-gray-500">
            {form.compatibility.length === 0 ? 'Aucune règle : le produit est universel (compatible avec tous les véhicules).' : 'Le produit est compatible si au moins une règle correspond au véhicule.'}
          </p>
          <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer" title="Les compatibilités héritées des pièces équivalentes sont mises en attente : vous les prévisualisez et validez (Produits › Compatibilités héritées) au lieu d'une fusion automatique.">
            <input type="checkbox" checked={!!form.compat_review} onChange={(e) => setForm(f => ({ ...f, compat_review: e.target.checked }))} className="rounded border-gray-300 text-orange-500" data-testid="compat-review-toggle" />
            Fiche sensible : valider les compatibilités héritées avant fusion
          </label>
          {pending && (pending.rules?.length || pending.fitments_added) ? (
            <p className="text-xs text-amber-700 mt-0.5" data-testid="compat-pending-hint">
              <AlertCircle size={12} className="inline mr-1" />{pending.rules?.length || 0} règle(s), {pending.models_added || 0} modèle(s) et {pending.fitments_added || 0} motorisation(s) héritées en attente de validation (Produits › Compatibilités héritées).
            </p>
          ) : null}
          {sources.length > 0 && (
            <p className="text-xs text-emerald-700 mt-0.5" data-testid="compat-sync-hint" title={sources.map(s => `${s.brand || ''} ${s.ref || ''}`.trim()).join(', ')}>
              <Check size={12} className="inline mr-1" />Complétée automatiquement depuis {sources.length} pièce(s) équivalente(s) : {sources.slice(0, 3).map(s => `${s.brand || ''} ${s.ref || ''}`.trim()).join(', ')}{sources.length > 3 ? '…' : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {productId && (
            <Button type="button" variant="outline" size="sm" onClick={inheritCompat} disabled={syncing} data-testid="compat-inherit-button" title="Reprend les marques, modèles et motorisations des pièces équivalentes (références partagées ou liens manuels)">
              {syncing ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} Hériter des équivalences
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={addRule} data-testid="compat-add-rule">
            <Plus size={14} className="mr-1" /> Ajouter une règle
          </Button>
        </div>
      </div>
      <div className="space-y-3">
        {form.compatibility.map((rule, idx) => (
          <CompatRuleEditor key={idx} index={idx} rule={rule} brands={brands} onChange={(r) => updateRule(idx, r)} onRemove={() => removeRule(idx)} />
        ))}
      </div>
    </div>
  );
};

export default ProductCompatSection;
